/**
 * Idempotent bulk import for legacy single-study-with-embedded-group JSON files.
 *
 * Each source file is parsed, fingerprinted by content hash, and matched against
 * existing studies by natural key (name + starting location). The decision per
 * source study:
 *   - new (no existing match): inserted with a deterministic id derived from
 *     the natural key so re-imports converge.
 *   - existing + same content hash: skipped (no-op).
 *   - existing + different content hash: overwritten (keeping the existing id +
 *     createdAt, refreshing updatedAt).
 *
 * Study groups are added only when their id is missing from local state — never
 * overwritten, so any local edits to member readingLevel/writingLevel survive.
 *
 * PDFs are NOT regenerated during bulk import (the writer goes through
 * `bulkSaveStudies`, which skips PDF sidecars by design). Users can regenerate
 * by editing each study, or via a future "rebuild PDFs" tool.
 */

import type { BibleLocation, Student, Study, StudyGroup, Teacher } from '@skjv/common';
import { bibleState } from '@/state/bible-state';
import { deriveStudyId, studyContentHash, studyNaturalKey } from './study-hash';

export interface BulkImportProgress {
  fileIndex: number;
  totalFiles: number;
  fileName: string;
  added: number;
  updated: number;
  skipped: number;
  errors: number;
}

export interface BulkImportError {
  fileName: string;
  reason: string;
}

export interface BulkImportResult {
  added: number;
  updated: number;
  skipped: number;
  errors: BulkImportError[];
  totalGroupsAdded: number;
}

export interface BulkImportOptions {
  /** When a local study has the same natural key but different content hash,
   *  overwrite it. Default true. */
  overwriteWhenContentDiffers?: boolean;
  onProgress?: (event: BulkImportProgress) => void;
}

interface LegacyStudyJson {
  name?: string;
  startingLocation?: BibleLocation;
  endingLocation?: BibleLocation;
  writingLocation?: BibleLocation;
  memoryStartingLocation?: BibleLocation;
  memoryEndingLocation?: BibleLocation;
  scenes?: Array<{ descriptions: string[] }>;
  assignments?: Array<{
    student: Student;
    reviewQuestion?: string | null;
    startingLocation?: BibleLocation;
    endingLocation?: BibleLocation;
  }>;
  teacherAssignments?: Array<{
    teacher: Teacher;
    reviewQuestion?: string | null;
  }>;
  studyGroup?: {
    id: string;
    name: string;
    description?: string;
    leaderCount?: number;
    teachers?: Teacher[];
    students?: Student[];
  };
}

interface ParsedSource {
  fileName: string;
  source: LegacyStudyJson;
  fileLastModified?: number;
}

export async function bulkImportLegacyFiles(
  files: File[],
  opts: BulkImportOptions = {},
): Promise<BulkImportResult> {
  const overwrite = opts.overwriteWhenContentDiffers ?? true;
  const errors: BulkImportError[] = [];
  const parsedSources: ParsedSource[] = [];

  // Parse phase
  for (const file of files) {
    if (!file.name.endsWith('.json')) continue;
    try {
      const text = await file.text();
      const data = JSON.parse(text) as LegacyStudyJson;
      if (!data.name || !data.startingLocation || !data.studyGroup) {
        errors.push({ fileName: file.name, reason: 'Not a legacy study JSON (missing name / startingLocation / studyGroup).' });
        continue;
      }
      parsedSources.push({ fileName: file.name, source: data, fileLastModified: file.lastModified });
    } catch (err) {
      errors.push({ fileName: file.name, reason: `Parse failed: ${(err as Error).message}` });
    }
  }

  // Match phase
  const existing = bibleState.studies;
  const existingByKey = new Map(existing.map((s) => [studyNaturalKey(s), s]));
  const studiesToWrite: Study[] = [];
  const groupsByIdToAdd = new Map<string, StudyGroup>();
  const existingGroupIds = new Set(bibleState.studyGroups.map((g) => g.id));

  let added = 0;
  let updated = 0;
  let skipped = 0;
  let fileIndex = 0;

  for (const parsed of parsedSources) {
    fileIndex++;
    try {
      const { study, group } = await buildStudyAndGroup(parsed);
      const naturalKey = studyNaturalKey(study);
      const existingStudy = existingByKey.get(naturalKey);

      if (existingStudy) {
        const [sourceHash, existingHash] = await Promise.all([
          studyContentHash(study),
          studyContentHash(existingStudy),
        ]);
        if (sourceHash === existingHash) {
          skipped++;
        } else if (overwrite) {
          studiesToWrite.push({
            ...study,
            id: existingStudy.id,
            createdAt: existingStudy.createdAt,
            updatedAt: new Date().toISOString(),
          });
          updated++;
        } else {
          skipped++;
        }
      } else {
        const id = await deriveStudyId(study);
        studiesToWrite.push({ ...study, id });
        added++;
      }

      if (!existingGroupIds.has(group.id) && !groupsByIdToAdd.has(group.id)) {
        groupsByIdToAdd.set(group.id, group);
      }

      opts.onProgress?.({
        fileIndex,
        totalFiles: parsedSources.length,
        fileName: parsed.fileName,
        added,
        updated,
        skipped,
        errors: errors.length,
      });
    } catch (err) {
      errors.push({ fileName: parsed.fileName, reason: (err as Error).message });
    }
  }

  // Commit phase
  if (studiesToWrite.length > 0 || groupsByIdToAdd.size > 0) {
    await bibleState.bulkImportStudies(studiesToWrite, Array.from(groupsByIdToAdd.values()));
  }

  return {
    added,
    updated,
    skipped,
    errors,
    totalGroupsAdded: groupsByIdToAdd.size,
  };
}

async function buildStudyAndGroup(parsed: ParsedSource): Promise<{ study: Study; group: StudyGroup }> {
  const { source, fileLastModified } = parsed;
  if (!source.name || !source.startingLocation || !source.studyGroup) {
    throw new Error('Missing required legacy fields');
  }
  const ts = fileLastModified ? new Date(fileLastModified).toISOString() : new Date().toISOString();

  const group: StudyGroup = {
    id: source.studyGroup.id,
    name: source.studyGroup.name,
    description: source.studyGroup.description,
    leaderCount: source.studyGroup.leaderCount ?? 1,
    teachers: source.studyGroup.teachers ?? [],
    students: source.studyGroup.students ?? [],
    updatedAt: ts,
  };

  const study: Study = {
    id: '', // filled by caller
    name: source.name,
    createdAt: ts,
    updatedAt: ts,
    startingLocation: source.startingLocation,
    endingLocation: source.endingLocation,
    studyGroupId: source.studyGroup.id,
    scenes: source.scenes ?? [],
    writingLocation: source.writingLocation ?? source.startingLocation,
    memoryStartingLocation: source.memoryStartingLocation,
    memoryEndingLocation: source.memoryEndingLocation,
    assignments: (source.assignments ?? []).map((a) => ({
      student: a.student,
      reviewQuestion: a.reviewQuestion ?? null,
      startingLocation: a.startingLocation,
      endingLocation: a.endingLocation,
    })),
    teacherAssignments: (source.teacherAssignments ?? []).map((t) => ({
      teacher: t.teacher,
      reviewQuestion: t.reviewQuestion ?? null,
    })),
  };

  return { study, group };
}
